import React, { useState, useEffect, useRef } from 'react';
import './chatbot.css';
import chatIcon from '../../assets/icons/chat.svg';
import aiIcon from '../../assets/icons/ai.svg';
import sendIcon from '../../assets/img/snd-button.png';

interface Message {
  content: string;
  isUser: boolean;
  isSystem?: boolean;
  timestamp: string;
}

interface SessionStatus {
  message_count: number;
  max_messages: number;
  remaining_messages: number;
  limit_reached: boolean;
  session_id: string;
  session_created: string;
  session_expires: string;
  time_remaining_hours: number;
  is_expired: boolean;
}

interface ConversationEntry {
  timestamp: string;
  user_message: string;
  ai_response: string;
}

const Chatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionStatus, setSessionStatus] = useState<SessionStatus | null>(null);
  const [conversationHistory, setConversationHistory] = useState<ConversationEntry[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
  const apiEndpoint = `${API_BASE}/chat`;
  const sessionEndpoint = `${API_BASE}/session-status`;
  const historyEndpoint = `${API_BASE}/conversation-history`;

  useEffect(() => {
    loadSessionStatus();
    loadConversationHistory();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const body = document.body;
    if (isOpen) {
      body.classList.add('no-scroll');
    } else {
      body.classList.remove('no-scroll');
    }
    return () => body.classList.remove('no-scroll');
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadSessionStatus = async () => {
    try {
      const response = await fetch(sessionEndpoint, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const status = await response.json();
        setSessionStatus(status);
      } else {
        console.error('[SESSION] Failed to load session status:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('[SESSION] Error loading status:', error);
    }
  };

  const loadConversationHistory = async () => {
    try {
      const response = await fetch(historyEndpoint, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConversationHistory(data.conversation_history || []);
        
        if (data.session_status) {
          setSessionStatus(data.session_status);
        }
        
        if (data.is_expired) {
          console.log('[HISTORY] Session expired, no history to display');
        } else {
          displayConversationHistory(data.conversation_history || []);
        }
      }
    } catch (error) {
      console.error('[HISTORY] Error loading conversation history:', error);
    }
  };

  const displayConversationHistory = (history: ConversationEntry[]) => {
    if (history.length === 0) {
      addWelcomeMessage();
      return;
    }

    const historyMessages: Message[] = [];
    historyMessages.push({
      content: 'Previous conversation',
      isUser: false,
      isSystem: true,
      timestamp: new Date().toISOString()
    });

    history.forEach(exchange => {
      historyMessages.push({
        content: exchange.user_message,
        isUser: true,
        timestamp: exchange.timestamp
      });
      historyMessages.push({
        content: exchange.ai_response,
        isUser: false,
        timestamp: exchange.timestamp
      });
    });

    historyMessages.push({
      content: 'Current session separator',
      isUser: false,
      isSystem: true,
      timestamp: new Date().toISOString()
    });

    setMessages(historyMessages);
  };

  const addWelcomeMessage = () => {
    setTimeout(() => {
      setMessages([{
        content: "Hello! I'm your AI legal assistant. How can I help you today?",
        isUser: false,
        timestamp: new Date().toISOString()
      }]);
    }, 500);
  };

  const resetChat = async () => {
    try {
      await fetch(`${API_BASE}/reset-session`, {
        method: 'POST',
        credentials: 'include'
      });
      setMessages([]);
      setConversationHistory([]);
      setSessionStatus(null);
      await loadSessionStatus();
      addWelcomeMessage();
    } catch (error) {
      console.error('Error resetting chat:', error);
    }
  };

  (window as any).resetChat = resetChat;

  const toggleChatbot = () => {
    setIsOpen(!isOpen);
  };

  const closeChatbot = () => {
    setIsOpen(false);
  };

  const addMessage = (content: string, isUser: boolean, isSystem: boolean = false) => {
    const newMessage: Message = {
      content,
      isUser,
      isSystem,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const addSystemBanner = (content: string) => {
    addMessage(`⚠️ ${content}`, false, true);
  };

  const sendMessage = async () => {
    if (!inputValue.trim()) return;

    if (sessionStatus && sessionStatus.limit_reached) {
      addSystemBanner('Message limit reached. Please download our app for unlimited access.');
      return;
    }

    const message = inputValue.trim();
    setInputValue('');
    setIsLoading(true);

    addMessage(message, true);

    const thinkingMessage: Message = {
      content: 'thinking',
      isUser: false,
      isSystem: false,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, thinkingMessage]);

    try {
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          message, 
          timestamp: new Date().toISOString() 
        })
      });

      setMessages(prev => prev.filter(msg => msg.content !== 'thinking'));

      const data = await response.json();

      if (response.status === 429 && data.limit_reached) {
        addSystemBanner('Message limit reached. Please download our app for unlimited access.');
        if (data.session_status) {
          setSessionStatus(data.session_status);
        }
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || 'Network response was not ok');
      }

      if (data.session_status) {
        setSessionStatus(data.session_status);
      } else {
        await loadSessionStatus();
      }

      addMessage(data.response || 'Sorry, no response available.', false);

    } catch (error) {
      setMessages(prev => prev.filter(msg => msg.content !== 'thinking'));
      addMessage('Sorry, something went wrong. Please try again.', false, true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getCounterClass = () => {
    if (!sessionStatus) return 'normal';
    const remaining = sessionStatus.remaining_messages;
    if (remaining <= 2) return 'critical';
    if (remaining <= 5) return 'warning';
    return 'normal';
  };

  const getCounterText = () => {
    if (!sessionStatus) return 'Loading...';
    if (sessionStatus.limit_reached) return 'Limit reached';
    const remaining = sessionStatus.remaining_messages;
    return `${remaining} message${remaining !== 1 ? 's' : ''} left`;
  };

  const isInputDisabled = sessionStatus && (sessionStatus.limit_reached || sessionStatus.is_expired);

  return (
    <>
      {/* Chat Toggle Button */}
      <button 
        className={`chat-toggle ${isOpen ? 'hidden' : ''}`}
        onClick={toggleChatbot}
      >
        <img src={chatIcon} alt="Chat" className="chat-toggle-icon" />
      </button>

      {/* Chatbox */}
      <div className={`ai-chatbox ${isOpen ? 'open' : ''}`}>
        <div className="chatbox-header">
          <div className="chatbox-title">
            <div className="ai-icon">
              <img src={aiIcon} alt="AI" className="ai-icon-img" />
            </div> Legal Assistant
          </div>
          <div className={`message-counter ${getCounterClass()}`}>
            <span>{getCounterText()}</span>
          </div>
          <button className="close-btn" onClick={closeChatbot}>×</button>
        </div>

        <div className="chatbox-messages">
          {messages.map((message, index) => (
            <div key={index}>
              {message.content === 'thinking' ? (
                <div className="message-wrapper ai">
                  <div className="message thinking">
                    <span>Thinking</span>
                    <div className="thinking-dots">
                      <div className="thinking-dot"></div>
                      <div className="thinking-dot"></div>
                      <div className="thinking-dot"></div>
                    </div>
                  </div>
                </div>
              ) : message.isSystem && (message.content.includes('conversation') || message.content.includes('separator')) ? (
                message.content.includes('Current') || message.content.includes('separator') ? (
                  <div className="simple-text-separator">
                    <span>Current session</span>
                  </div>
                ) : (
                  <div className="conversation-separator">
                    <span>{message.content}</span>
                  </div>
                )
              ) : message.isSystem && message.content.startsWith('⚠️') ? (
                <div className="system-banner">
                  <strong>{message.content}</strong>
                </div>
              ) : (
                <div className={`message-wrapper ${message.isUser ? 'user' : 'ai'}`}>
                  <div className={`message ${message.isUser ? 'user' : 'ai'} ${message.isSystem ? 'system' : ''}`}>
                    <div className="message-content">{message.content}</div>
                    <div className="message-time">
                      {formatTime(message.timestamp)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div className="chatbox-input">
          <input
            type="text"
            className="message-input"
            placeholder={isInputDisabled ? 'Message limit reached.' : 'Type your message...'}
            title={isInputDisabled ? 'Please download our app for unlimited access' : ''}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isInputDisabled || isLoading}
            maxLength={500}
          />
          <button 
            className="send-btn"
            onClick={sendMessage}
            disabled={isInputDisabled || isLoading || !inputValue.trim()}
          >
            <img src={sendIcon} alt="Send" className="send-btn-icon" />
          </button>
        </div>
      </div>
    </>
  );
};

export default Chatbot;